<!-- Minimal Operator shell (#205): slim top bar + narrow left rail. Deliberately NOT app-navbar-office — an
     Operator session holds no admin permissions, so that chrome would render mostly empty and its data calls
     would 403. -->
<div class="fixed inset-x-0 top-0 z-40 flex h-14 items-center border-b border-gray-200/50 bg-white/80 px-4
            shadow-sm backdrop-blur-md">
  <button (click)="sidebar = !sidebar" type="button"
          class="mr-2 rounded-lg p-2 text-gray-600 hover:bg-gray-100/80 lg:hidden">
    <svg class="h-5 w-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path d="M3 18v-2h18v2zm0-5v-2h18v2zm0-5V6h18v2z"/>
    </svg>
    <span class="sr-only">Menü</span>
  </button>

  <img alt="CheqMe" class="h-8 w-8 rounded-full border border-gray-200" src="assets/images/icon.png">
  <div class="ml-3 leading-tight">
    <p class="text-sm font-bold tracking-tight text-gray-900">CheqMe</p>
    <p class="text-xs text-gray-500">{{ user?.name || 'Betriebsleitung' }}</p>
  </div>

  <!--
    Cookie settings live HERE rather than in the rail: below `lg` the rail is a drawer behind the menu button,
    and the consent dialog has to stay one tap away at phone widths too. This is the shell's only route back to
    it — the app-wide entry point sits in `navbar-help`, inside the `app-navbar-office` chrome this shell
    deliberately does not render. Icon-only and muted, so it reads as subordinate to Abmelden.
  -->
  <button (click)="showCookieSettings()" type="button" title="Cookie-Einstellungen"
          class="ml-auto inline-flex items-center rounded-lg p-2 text-gray-400 transition-all duration-200
                 hover:bg-gray-100/80 hover:text-gray-900">
    <svg aria-hidden="true" class="h-5 w-5" fill="currentColor" viewBox="0 0 32 32"
         xmlns="http://www.w3.org/2000/svg">
      <circle cx="10" cy="21" r="2"/>
      <circle cx="23" cy="20" r="2"/>
      <circle cx="13" cy="10" r="2"/>
      <circle cx="14" cy="15" r="1"/>
      <circle cx="23" cy="5" r="2"/>
      <circle cx="29" cy="3" r="1"/>
      <circle cx="16" cy="23" r="1"/>
      <path
        d="M16 30C8.3 30 2 23.7 2 16S8.3 2 16 2h.3l1.4.1l-.3 1.2c-.1.4-.2.9-.2 1.3c0 2.8 2.2 5 5 5c1 0 2-.3 2.9-.9l1.3 1.5c-.4.4-.6.9-.6 1.4c0 1.3 1.3 2.4 2.7 1.9l1.2-.5l.2 1.3c.1.6.1 1.2.1 1.7c0 7.7-6.3 14-14 14m-.7-26C9 4.4 4 9.6 4 16c0 6.6 5.4 12 12 12s12-5.4 12-12v-.4c-2.3.1-4.2-1.7-4.2-4v-.2c-.5.1-1 .2-1.6.2c-3.9 0-7-3.1-7-7c0-.2 0-.4.1-.6"/>
    </svg>
    <span class="sr-only">Cookie-Einstellungen</span>
  </button>

  <button (click)="logout()" type="button"
          class="ml-1 inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium
                 text-gray-500 transition-all duration-200 hover:bg-gray-100/80 hover:text-gray-900">
    <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h6a2 2 0 012 2v1"
            stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8"/>
    </svg>
    Abmelden
  </button>
</div>

<!-- Mobile-only backdrop — closes the rail on outside tap. -->
<div (click)="sidebar = false"
     [class.pointer-events-auto]="sidebar" [class.opacity-50]="sidebar"
     class="fixed inset-x-0 bottom-0 top-14 z-30 cursor-pointer bg-gray-900 opacity-0 pointer-events-none
            transition-opacity lg:hidden"></div>

<div [class.translate-x-0]="sidebar"
     class="op-rail fixed bottom-0 left-0 top-14 z-40 w-64 -translate-x-full border-r border-gray-200/60 bg-white
            p-2 shadow-md transition-transform duration-200 lg:translate-x-0 lg:bg-white/60 lg:shadow-none
            lg:backdrop-blur-sm">
  <!--
    The rail is drag-resizable, but only on `lg` and up — below that it is an overlay drawer whose width is the
    viewport's business, not the user's, so the handle is not rendered at all (rather than merely hidden) and can
    never become a stray tab stop on a phone. `desktop()` tracks the same 1024 px the `lg:` classes above do.
  -->
  @if (desktop()) {
    <!--
      The handle is placed by this wrapper rather than by utility classes on the component itself: the component's
      own `:host` rule sets `position: relative`, and an encapsulated `:host` selector beats a same-specificity
      Tailwind utility, so `class="absolute"` on <app-resize-handle> would silently do nothing at all.
    -->
    <div class="absolute inset-y-0 right-0 flex">
      <app-resize-handle edge="end" label="Breite der Navigation ändern"
                         [width]="railWidth()" (widthChange)="requestedRailWidth.set($event)"
                         [min]="railMinWidth" [max]="railMaxWidth()" [showDivider]="false"
                         (committed)="persistRailWidth($event)"/>
    </div>
  }

  <nav aria-label="Operator-Navigation" class="flex flex-col gap-y-0.5">
    <app-sidebar-button [routerLink]="[OperatorPanelRoutes.OpenTables]" title="Tische">
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M3 6h13M3 12h13M3 18h9" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8"/>
      </svg>
    </app-sidebar-button>

    <!--
      Future entry: the kitchen screen ("Küche" — a McDonald's-style "what to cook next" view, #205 decision).
      This is the whole reason Operator gets its own panel instead of a single route: the next live-service
      screen slots in here as one more sidebar entry, not a new role. Left visible-but-disabled so an operator
      can see it is coming, rather than having it silently absent. Once built, replace this block with
      `<app-sidebar-button [routerLink]="[OperatorPanelRoutes.Kitchen]" title="Küche">…</app-sidebar-button>`
      and uncomment the matching route in operator.routes.ts / OperatorPanelRoutes.
    -->
    <div aria-disabled="true" title="Küche — bald verfügbar"
         class="mt-2 grid cursor-not-allowed select-none grid-cols-4 items-center gap-x-1 rounded-lg p-2
                text-gray-300 lg:mt-1 lg:p-1.5">
      <div class="col-span-1 flex">
        <div class="m-auto h-7 w-7 lg:h-6 lg:w-6">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 7h16M4 7a2 2 0 012-2h12a2 2 0 012 2M4 7v11a2 2 0 002 2h12a2 2 0 002-2V7M9 11v4m3-4v4m3-4v4"
                  stroke-linecap="round" stroke-linejoin="round" stroke-width="1.6"/>
          </svg>
        </div>
      </div>
      <div class="col-span-3 flex">
        <div class="-space-y-1 flex flex-col place-self-center">
          <span class="lg:text-sm">Küche</span>
          <span class="text-sm text-gray-300 lg:text-xs">Bald verfügbar</span>
        </div>
      </div>
    </div>
  </nav>
</div>

<div class="op-content fixed inset-x-0 bottom-0 top-14 lg:left-64">
  <router-outlet></router-outlet>
</div>
